import { useState, useEffect, useMemo, useRef } from 'react';
import { Search, ChevronDown, Calendar, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { STOCK_LIST, loadStockList, searchStocks } from '../../data/stockList';
import { BuyOrders } from './orderbook-replay/BuyOrders';
import { OrderBook } from './orderbook-replay/OrderBook';
import { SellOrders } from './orderbook-replay/SellOrders';
import { TradeHistory } from './orderbook-replay/TradeHistory';
import { DoneDetails } from './orderbook-replay/DoneDetails';
import { MediaPlayer } from './orderbook-replay/MediaPlayer';
import { api } from '../../services/api';
import { loadOrderDetails, loadOhlcByPath, loadDoneDetails, parseOrderDetails, parseDoneDetails } from '../../services/duckdbService';
import { fetchZipEntry } from '../../utils/zipRangeReader';
import { resolveBoBundleUrls } from '../../utils/boBundleBlob';
import { getOhlcBlobPath } from '../../utils/sectorMappingCache';
import { StockInfoHeader } from './orderbook-replay/StockInfoHeader';
import { useProfile } from '../../contexts/ProfileContext';
import { useToast } from '../../contexts/ToastContext';

const TRIAL_MAX_PLAYS = 3;

// Convert "HH:MM" → seconds since 00:00 relative to 09:00:00 (32400 seconds)
const timeToSecondsRelative = (timeStr: string): number => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':').map(Number);
  const h = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  const s = parts[2] ?? 0;
  return (h * 3600 + m * 60 + s) - 32400;
};

// Orderbook freeze 15:59 — running trade tetap jalan s/d 16:30.
const ORDERBOOK_FREEZE_SEC = timeToSecondsRelative('15:59:00');

// ── Panel filter ─────────────────────────────────────────────────────────
type PanelFilter = { price: string; broker: string; message: string };
const EMPTY_FILTER: PanelFilter = { price: '', broker: '', message: '' };
const MSG_OPTIONS: { value: string; label: string }[] = [
  { value: 'Open', label: 'Open' },
  { value: 'Full Matched', label: 'Full Matched' },
  { value: 'Partial Matched', label: 'Part Matched' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'Withdrawn Match', label: 'WD Matched' },
  { value: 'Amended', label: 'Amended' },
];

function FilterBar({ label, filter, setFilter, onApply, accent, hideStatusSelect, customButton }: {
  label: string;
  filter: PanelFilter;
  setFilter: (f: PanelFilter) => void;
  onApply: () => void;
  accent?: 'buy' | 'sell';
  hideStatusSelect?: boolean;
  customButton?: React.ReactNode;
}) {
  const bg = accent === 'buy' ? 'bg-green-800/60' : accent === 'sell' ? 'bg-red-800/60' : 'bg-gray-900';
  return (
    <div className={`${bg} border-b border-gray-700 px-5 py-2 flex items-center gap-2 shrink-0`}>
      <span className="font-semibold text-[17.5px] text-gray-400 whitespace-nowrap mr-1.5">{label}</span>
      <input
        type="text"
        placeholder="Price"
        value={filter.price}
        onChange={e => setFilter({ ...filter, price: e.target.value })}
        onKeyDown={e => e.key === 'Enter' && onApply()}
        className="h-[25px] w-20 px-1.5 bg-gray-800 border border-gray-600 rounded text-[14px] text-white placeholder:text-gray-500 focus:outline-none focus:border-gray-400"
      />
      <input
        type="text"
        placeholder="Broker"
        value={filter.broker}
        onChange={e => setFilter({ ...filter, broker: e.target.value })}
        onKeyDown={e => e.key === 'Enter' && onApply()}
        className="h-[25px] w-[70px] px-1.5 bg-gray-800 border border-gray-600 rounded text-[14px] text-white placeholder:text-gray-500 focus:outline-none focus:border-gray-400"
      />
      {!hideStatusSelect && (
        <select
          value={filter.message}
          onChange={e => setFilter({ ...filter, message: e.target.value })}
          className={`h-[25px] w-20 px-1.5 bg-gray-800 border border-gray-600 rounded text-[14px] focus:outline-none appearance-none ${filter.message === '' ? 'text-gray-500' : 'text-white'}`}
        >
          <option value="">Status</option>
          {MSG_OPTIONS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      )}
      {customButton}
      <button
        onClick={onApply}
        className="h-[25px] px-2.5 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-bold rounded whitespace-nowrap"
      >
        Show
      </button>
    </div>
  );
}

// ── LocalStorage helpers for state persistence ────────────────────────────
const LS_KEY = 'orderbook_replay_state';

function loadPersistedState(isDashboard: boolean) {
  try {
    const key = isDashboard ? `dashboard_${LS_KEY}` : LS_KEY;
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { }
  return null;
}

interface BrokerSelectorProps {
  selectedBrokers: string[];
  setSelectedBrokers: React.Dispatch<React.SetStateAction<string[]>>;
  availableBrokers: string[];
  isTrial: boolean;
  showToast: (options: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message: string }) => void;
}

function BrokerSelector({
  selectedBrokers,
  setSelectedBrokers,
  availableBrokers,
  isTrial,
  showToast
}: BrokerSelectorProps) {
  const [brokerSearch, setBrokerSearch] = useState('');
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  const dropdownBrokerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownBrokerRef.current && !dropdownBrokerRef.current.contains(event.target as Node)) {
        setShowBrokerSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredBrokers = useMemo(() => {
    if (!brokerSearch) return availableBrokers;
    return availableBrokers.filter(b => b.toLowerCase().includes(brokerSearch.toLowerCase()));
  }, [availableBrokers, brokerSearch]);

  return (
    <div className="relative" ref={dropdownBrokerRef}>
      <div
        className="w-16 sm:w-24 h-8 sm:h-9 px-2 sm:px-3 flex items-center justify-between bg-background border border-[#3a4252] rounded-md text-[13px] sm:text-sm text-white cursor-pointer hover:border-primary/50"
        onClick={() => {
          if (isTrial) {
            showToast({
              type: 'warning',
              title: 'Fitur Terbatas',
              message: 'Dalam masa free trial, Anda tidak dapat memilih broker spesifik.'
            });
            return;
          }
          setShowBrokerSuggestions(!showBrokerSuggestions);
        }}
      >
        <span className="truncate text-sm">
          {selectedBrokers.includes('ALL') ? 'All' : selectedBrokers.join(', ')}
        </span>
        <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
      </div>

      {!isTrial && showBrokerSuggestions && (
        <div className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-[5001] w-48 overflow-hidden flex flex-col">
          {selectedBrokers.length > 0 && !selectedBrokers.includes('ALL') && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
              <span>Selected ({selectedBrokers.length})</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBrokers(['ALL']);
                }}
                className="text-xs text-red-400 hover:text-red-300 font-medium"
              >
                Clear
              </button>
            </div>
          )}
          <div className="p-2 border-b border-[#3a4252]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                type="text"
                className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                placeholder="Search broker..."
                value={brokerSearch}
                onChange={(e) => setBrokerSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {brokerSearch === '' && (
              <div
                className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${selectedBrokers.includes('ALL') ? 'text-primary font-medium' : 'text-white'}`}
                onClick={() => setSelectedBrokers(['ALL'])}
              >
                <span>ALL</span>
                {selectedBrokers.includes('ALL') && <span className="text-primary text-xs">✓</span>}
              </div>
            )}
            {filteredBrokers.slice(0, 30).map(broker => {
              const isSelected = selectedBrokers.includes(broker);
              return (
                <div
                  key={broker}
                  className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'text-white'}`}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedBrokers(prev => {
                      const without = prev.filter(b => b !== 'ALL');
                      if (isSelected) {
                        const updated = without.filter(b => b !== broker);
                        return updated.length === 0 ? ['ALL'] : updated;
                      }
                      return [...without, broker];
                    });
                  }}
                >
                  <span>{broker}</span>
                  {isSelected && <span className="text-primary text-xs">✓</span>}
                </div>
              );
            })}
            {filteredBrokers.length > 30 && (
              <div className="px-3 py-1.5 text-xs text-muted-foreground border-t border-[#3a4252] text-center">
                ... and {filteredBrokers.length - 30} more brokers
              </div>
            )}
            {filteredBrokers.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">No brokers found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function OrderbookReplay({ selectedStock: propSelectedStock, isDashboard = false, isActive = true }: { selectedStock?: string, isDashboard?: boolean, isActive?: boolean } = {}) {
  const { profile } = useProfile();
  const { showToast } = useToast();
  const isTrial = profile?.subscriptionStatus === 'trial';

  // Trial play count state
  const [trialPlayCount, setTrialPlayCount] = useState<number>(0);
  const [trialPlayCountLoaded, setTrialPlayCountLoaded] = useState(false);

  const [persisted] = useState(() => loadPersistedState(isDashboard));

  // Tracks whether the component is still mounted, so in-flight fetches (handleShow, etc.)
  // can bail out instead of setting state after the user switches tabs/unmounts.
  const isMountedRef = useRef(true);
  const [isFullDataLoaded, setIsFullDataLoaded] = useState(true);
  const backgroundLoaderAbortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (backgroundLoaderAbortControllerRef.current) {
        backgroundLoaderAbortControllerRef.current.abort();
      }
    };
  }, []);

  // ── Control panel state ──────────────────────────────────────────────────
  const [stockInput, setStockInput] = useState(propSelectedStock ?? persisted?.stockInput ?? 'AADI');
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const panelWrapperRef = useRef<HTMLDivElement>(null);
  const [panelScale, setPanelScale] = useState(1);
  const [panelOuterH, setPanelOuterH] = useState(0);
  const PANEL_TARGET_WIDTH = 2100; // natural width in px; below this, scale down proportionally

  const [targetDate, setTargetDate] = useState(() => {
    if (!isDashboard && persisted?.targetDate) return persisted.targetDate;
    return '';
  });

  const [mobileTab, setMobileTab] = useState<'buy' | 'orderbook' | 'sell' | 'trade'>('orderbook');

  const [startTime, setStartTime] = useState(persisted?.startTime ?? '09:00:00');
  const [endTime, setEndTime] = useState(persisted?.endTime ?? '16:30:00');

  const [selectedBoard] = useState(persisted?.selectedBoard ?? 'RG');
  const [selectedFD, setSelectedFD] = useState(persisted?.selectedFD ?? 'All');
  const [selectedBrokers, setSelectedBrokers] = useState<string[]>(persisted?.selectedBrokers ?? ['ALL']);
  const [availableBrokers, setAvailableBrokers] = useState<string[]>([]);

  // Applied state (only updates on "Show")
  const [appliedStock, setAppliedStock] = useState(propSelectedStock ?? persisted?.appliedStock ?? 'AADI');
  const [appliedStartSec, setAppliedStartSec] = useState(persisted?.appliedStartSec ?? 0);
  const [appliedEndSec, setAppliedEndSec] = useState(persisted?.appliedEndSec ?? 7.5 * 3600);
  const [appliedBoard, setAppliedBoard] = useState(persisted?.appliedBoard ?? 'RG');
  const [appliedFD, setAppliedFD] = useState(persisted?.appliedFD ?? 'All');

  // ── Replay engine state ──────────────────────────────────────────────────
  const [isPlaying, setIsPlaying] = useState(false);

  // Helper to attempt play — enforces trial limit for free trial users
  // Defined here (after setIsPlaying) to avoid use-before-declaration
  const isStartingPlayRef = useRef(false);

  const attemptPlay = async () => {
    if (isStartingPlayRef.current) return;

    if (!isTrial) {
      setIsPlaying(true);
      return;
    }

    isStartingPlayRef.current = true;
    try {
      const remaining = TRIAL_MAX_PLAYS - trialPlayCount;
      if (remaining <= 0) {
        showToast({
          type: 'warning',
          title: 'Fitur Terbatas',
          message: 'Batas 3 kali pemutaran replay selama FREE TRIAL telah habis. Silakan tingkatkan langganan Anda.'
        });
        return;
      }

      const res = await api.incrementOrderbookReplayPlay();
      if (!res.success || !res.data) {
        showToast({ type: 'error', title: 'Error', message: 'Gagal mencatat pemutaran.' });
        return;
      }

      setTrialPlayCount(res.data.count);

      setIsPlaying(true);
    } finally {
      isStartingPlayRef.current = false;
    }
  };
  const [speed, setSpeed] = useState(persisted?.speed ?? 1);
  const [currentTime, setCurrentTime] = useState(persisted?.currentTime ?? persisted?.appliedStartSec ?? 0);
  const [isLoading, setIsLoading] = useState(false);
  const [realOrders, setRealOrders] = useState<any[]>([]);
  const [realDoneOrders, setRealDoneOrders] = useState<any[]>([]);
  const [prevClose, setPrevClose] = useState<number | null>(null);

  // ── Panel-level filters ───────────────────────────────────────────────────
  const [buyFilter, setBuyFilter] = useState<PanelFilter>(persisted?.buyFilter ?? EMPTY_FILTER);
  const [buyApplied, setBuyApplied] = useState<PanelFilter>(persisted?.buyApplied ?? EMPTY_FILTER);
  const [sellFilter, setSellFilter] = useState<PanelFilter>(persisted?.sellFilter ?? EMPTY_FILTER);
  const [sellApplied, setSellApplied] = useState<PanelFilter>(persisted?.sellApplied ?? EMPTY_FILTER);
  const [tradeFilter, setTradeFilter] = useState<PanelFilter>(persisted?.tradeFilter ?? EMPTY_FILTER);
  const [tradeApplied, setTradeApplied] = useState<PanelFilter>(persisted?.tradeApplied ?? EMPTY_FILTER);
  const [isTradeGrouped, setIsTradeGrouped] = useState<boolean>(persisted?.isTradeGrouped ?? true);
  const [showBroker, setShowBroker] = useState<boolean>(persisted?.showBroker ?? true);
  const [showOrderNo, setShowOrderNo] = useState<boolean>(persisted?.showOrderNo ?? true);
  const [appliedShowBroker, setAppliedShowBroker] = useState<boolean>(persisted?.appliedShowBroker ?? true);
  const [appliedShowOrderNo, setAppliedShowOrderNo] = useState<boolean>(persisted?.appliedShowOrderNo ?? true);

  const tradeHistoryRef = useRef<{ collapseAll: () => void }>(null);

  const maxTime = appliedEndSec;

  useEffect(() => {
    if (propSelectedStock) {
      setStockInput(propSelectedStock);
      // We don't automatically set appliedStock here, handleShow does it
    }
  }, [propSelectedStock]);

  // Auto-trigger data fetch when isDashboard is true
  const dashboardAutoFetchRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isDashboard || !propSelectedStock || !isActive) return;
    if (dashboardAutoFetchRef.current === propSelectedStock) return;

    const timer = setTimeout(() => {
      if (targetDate) {
        dashboardAutoFetchRef.current = propSelectedStock;
        handleShow();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isDashboard, propSelectedStock, targetDate, isActive]);

  // ── Persist state to localStorage on every relevant change ───────────────
  useEffect(() => {
    const state = {
      stockInput, targetDate, startTime, endTime,
      selectedBoard, selectedFD, selectedBrokers,
      appliedStock, appliedStartSec, appliedEndSec, appliedBoard, appliedFD,
      speed, currentTime,
      buyFilter, buyApplied,
      sellFilter, sellApplied,
      tradeFilter, tradeApplied,
      isTradeGrouped,
      showBroker,
      showOrderNo,
      appliedShowBroker,
      appliedShowOrderNo,
    };
    if (isDashboard) {
      delete (state as any).startTime;
      delete (state as any).endTime;
    }
    const key = isDashboard ? `dashboard_${LS_KEY}` : LS_KEY;
    localStorage.setItem(key, JSON.stringify(state));
  }, [
    stockInput, targetDate, startTime, endTime,
    selectedBoard, selectedFD, selectedBrokers,
    appliedStock, appliedStartSec, appliedEndSec, appliedBoard, appliedFD,
    speed, currentTime,
    buyFilter, buyApplied,
    sellFilter, sellApplied,
    tradeFilter, tradeApplied,
    isTradeGrouped,
    showBroker,
    showOrderNo,
    appliedShowBroker,
    appliedShowOrderNo,
  ]);

  // Load stock list on mount; fetch latest date for dashboards or when no persisted date
  useEffect(() => {
    loadStockList();
    if (isDashboard || !persisted?.targetDate) {
      api.getOrderbookReplayLatestDate().then(res => {
        if (!isMountedRef.current) return;
        if (res.success && res.data?.latestDate) {
          setTargetDate(res.data.latestDate);
        }
      });
    }
    // Fetch trial play count on mount (for trial users)
    api.getOrderbookReplayPlayCount().then(res => {
      if (!isMountedRef.current) return;
      if (res.success && res.data) {
        setTrialPlayCount(res.data.count);
      }
      setTrialPlayCountLoaded(true);
    }).catch(() => setTrialPlayCountLoaded(true));
  }, []);

  // Click outside to close dropdowns
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowStockSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch broker list
  useEffect(() => {
    let isMounted = true;
    const fetchBrokers = async () => {
      try {
        const cached = localStorage.getItem('broker-list-cache');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.brokers && isMounted) setAvailableBrokers(parsed.brokers);
        }
        const response = await api.getBrokerList();
        if (response.success && response.data?.brokers) {
          if (isMounted) setAvailableBrokers(response.data.brokers);
          localStorage.setItem('broker-list-cache', JSON.stringify({ brokers: response.data.brokers, timestamp: Date.now() }));
        }
      } catch { }
    };
    fetchBrokers();
    return () => { isMounted = false; };
  }, []);

  // Space bar play / pause
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.repeat) return; // Ignore hold-down repeat events
      if (e.code === 'Space' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLSelectElement)) {
        e.preventDefault();
        // If currently playing, always allow pause
        if (isPlaying) {
          setIsPlaying(false);
        } else {
          attemptPlay();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, isTrial, trialPlayCount]);


  // Playback tick — skip gaps > 30 s with no orders
  const GAP_SKIP_THRESHOLD = 30;
  const orderTimesRef = useRef<number[]>([]);
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setCurrentTime((prev: number) => {
        const next = prev + speed;
        if (next >= maxTime) { setIsPlaying(false); return maxTime; }

        // Find next order time after prev
        const times = orderTimesRef.current;
        let lo = 0, hi = times.length - 1, nextOrderTime: number | undefined;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if ((times[mid] as number) > prev) { nextOrderTime = times[mid]; hi = mid - 1; }
          else lo = mid + 1;
        }

        // If gap to next order exceeds threshold, jump directly to it
        if (nextOrderTime !== undefined && nextOrderTime - prev > GAP_SKIP_THRESHOLD) {
          return Math.min(nextOrderTime, maxTime);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isPlaying, speed, maxTime]);

  // Scale panels proportionally when container is narrower than target width
  useEffect(() => {
    const el = panelWrapperRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setPanelOuterH(height);
      // Disable scale on mobile to allow tabbed view at full width
      if (window.innerWidth < 1024) {
        setPanelScale(1);
      } else {
        setPanelScale(width >= PANEL_TARGET_WIDTH ? 1 : width / PANEL_TARGET_WIDTH);
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const filteredStocks = searchStocks(stockInput);

  const triggerBackgroundLoad = async (
    formattedStock: string,
    fullPromise: Promise<[Uint8Array | null, Uint8Array | null, Uint8Array | null]>,
    signal: AbortSignal
  ) => {
    try {
      console.log('[OrderbookReplay] Background loader: Awaiting concurrent full ZIP download...');
      const [fullOrderBytes, fullDoneBytes] = await fullPromise;

      if (signal.aborted) return;

      if (!fullOrderBytes) {
        throw new Error('Full order_detail.parquet not found in ZIP');
      }

      console.log('[OrderbookReplay] Background loader: Starting Web Worker...');
      const worker = new Worker(
        new URL('./orderbook-replay/lazyLoadWorker.ts', import.meta.url),
        { type: 'module' }
      );

      worker.onmessage = (e: MessageEvent) => {
        if (signal.aborted) {
          worker.terminate();
          return;
        }

        const res = e.data;
        if (res.success) {
          console.log('[OrderbookReplay] Background loader: Worker success! Received', res.syntheticEvents.length, 'events');

          if (!isMountedRef.current) {
            worker.terminate();
            return;
          }

          // Update state with full datasets
          setRealOrders(res.syntheticEvents);
          if (res.doneParsed) {
            setRealDoneOrders(res.doneParsed);
          }

          setIsFullDataLoaded(true);
        } else {
          console.error('[OrderbookReplay] Background loader: Worker error:', res.error);
          // Fallback to main thread parsing if worker fails
          runMainThreadFallback(fullOrderBytes, fullDoneBytes, formattedStock, signal);
        }
        worker.terminate();
      };

      worker.onerror = (err) => {
        console.error('[OrderbookReplay] Background loader: Worker error event:', err);
        // Fallback to main thread parsing if worker fails
        runMainThreadFallback(fullOrderBytes, fullDoneBytes, formattedStock, signal);
        worker.terminate();
      };

      worker.postMessage({
        orderZipBytes: fullOrderBytes,
        doneZipBytes: fullDoneBytes,
        formattedStock
      });

    } catch (err: any) {
      if (signal.aborted) return;
      console.error('[OrderbookReplay] Background loader failed:', err);
      showToast({
        type: 'error',
        title: 'Lazy Load Gagal',
        message: `Gagal memuat data lengkap secara background: ${err.message}`
      });
      setIsFullDataLoaded(true);
    }
  };

  const runMainThreadFallback = async (
    orderBytes: Uint8Array,
    doneBytes: Uint8Array | null,
    formattedStock: string,
    signal: AbortSignal
  ) => {
    try {
      console.log('[OrderbookReplay] Running main thread fallback parsing...');
      const rows = await parseOrderDetails(orderBytes.buffer);
      if (signal.aborted) return;

      let doneRows: any[] = [];
      if (doneBytes) {
        doneRows = await parseDoneDetails(doneBytes.buffer);
      }
      if (signal.aborted) return;

      // Map/Parse order rows to matching format
      const orderBrokerMap: Record<string, string> = {};
      for (const row of rows) {
        const side = row.bs || 'B';
        const oNum = String(Number(row.order_no) || row.order_no).trim();
        const brk = String(row.brk || '').trim();
        if (brk && brk !== '--' && oNum) {
          orderBrokerMap[`${oNum}_${side}`] = brk;
        }
      }
      for (const d of doneRows) {
        const o1 = String(Number(d.TRX_ORD1) || d.TRX_ORD1 || '').trim();
        const b1 = String(d.BRK_COD1 || '').trim();
        if (b1 && b1 !== '--' && o1) orderBrokerMap[`${o1}_B`] = b1;

        const o2 = String(Number(d.TRX_ORD2) || d.TRX_ORD2 || '').trim();
        const b2 = String(d.BRK_COD2 || '').trim();
        if (b2 && b2 !== '--' && o2) orderBrokerMap[`${o2}_S`] = b2;
      }

      let parsed = rows.map((row, index) => {
        const timeOffset = timeToSecondsRelative(row.time);
        const side = (row.bs || 'B') as 'B' | 'S';
        const orderNoNum = String(Number(row.order_no) || row.order_no).trim();
        let broker = String(row.brk || '').trim();
        if (!broker || broker === '--') {
          broker = orderBrokerMap[`${orderNoNum}_${side}`] || '--';
        }
        const counterSide = side === 'B' ? 'S' : 'B';
        const counterBroker = orderBrokerMap[`${orderNoNum}_${counterSide}`] || '--';
        return {
          id: row.queue || index,
          queue: row.queue,
          orderNo: orderNoNum,
          time: row.time,
          timeValue: timeOffset,
          stockCode: row.stock,
          buySell: side,
          board: row.board,
          price: Number(row.price),
          open: Number(row.opn),
          lot: Number(row.rls) || 0,
          release: Number(row.rls),
          foreignDomestic: row.inv === 'F' ? 'F' : 'D',
          brokerCode: broker,
          message: row.message,
          buyerBroker: side === 'B' ? broker : counterBroker,
          sellerBroker: side === 'S' ? broker : counterBroker,
        };
      });

      // ONLY KEEP RG ORDERS
      parsed = parsed.filter(p => p.board === 'RG');

      parsed.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        const aOrd = Number(a.orderNo) || Infinity;
        const bOrd = Number(b.orderNo) || Infinity;
        return aOrd - bOrd;
      });

      let doneParsed: any[] = [];
      if (doneRows.length > 0) {
        doneParsed = doneRows.map((row, index) => {
          let timeStr = String(row.TRX_TIME || '');
          if (timeStr.length === 5) timeStr = `0${timeStr}`;
          const formattedTime = timeStr.length === 6 ? `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}` : timeStr;
          const timeOffset = timeToSecondsRelative(formattedTime);
          const isBuy = row.HAKA_HAKI === 1 || row.HAKA_HAKI === '1';
          const bs = isBuy ? 'B' : 'S';
          return {
            id: `done_${index}`,
            time: formattedTime,
            timeValue: timeOffset,
            stockCode: formattedStock,
            buySell: bs,
            board: row.TRX_TYPE || row.BOARD || 'RG',
            price: Number(row.STK_PRIC),
            lot: (Number(row.STK_VOLM) || 0) / 100,
            foreignDomestic: ['F', 'A'].includes(String(row.INV_TYP1 || '').trim().toUpperCase()) ? 'F' : 'D',
            foreignDomesticBuyer: ['F', 'A'].includes(String(row.INV_TYP1 || '').trim().toUpperCase()) ? 'F' : 'D',
            foreignDomesticSeller: ['F', 'A'].includes(String(row.INV_TYP2 || '').trim().toUpperCase()) ? 'F' : 'D',
            buyerBroker: String(row.BRK_COD1 || '--'),
            sellerBroker: String(row.BRK_COD2 || '--'),
            buyerOrderNo: String(Number(row.TRX_ORD1) || row.TRX_ORD1 || ''),
            sellerOrderNo: String(Number(row.TRX_ORD2) || row.TRX_ORD2 || ''),
            message: 'Full Matched',
            brokerCode: String(row.BRK_COD1 || '--'),
          };
        });

        // ONLY KEEP RG TRADES
        doneParsed = doneParsed.filter(d => d.board === 'RG');

        doneParsed.sort((a, b) => {
          if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
          const aMinOrd = Math.min(Number(a.buyerOrderNo) || Infinity, Number(a.sellerOrderNo) || Infinity);
          const bMinOrd = Math.min(Number(b.buyerOrderNo) || Infinity, Number(b.sellerOrderNo) || Infinity);
          return aMinOrd - bMinOrd;
        });
      }

      if (signal.aborted) return;

      const doneTradesByPrice = new Map<number, any[]>();
      const doneByOrder = new Map<string, any[]>();
      for (const d of doneParsed) {
        if (!doneTradesByPrice.has(d.price)) doneTradesByPrice.set(d.price, []);
        doneTradesByPrice.get(d.price)!.push(d);
        if (d.buyerOrderNo) {
          if (!doneByOrder.has(d.buyerOrderNo)) doneByOrder.set(d.buyerOrderNo, []);
          doneByOrder.get(d.buyerOrderNo)!.push({ timeValue: d.timeValue, lot: d.lot });
        }
        if (d.sellerOrderNo) {
          if (!doneByOrder.has(d.sellerOrderNo)) doneByOrder.set(d.sellerOrderNo, []);
          doneByOrder.get(d.sellerOrderNo)!.push({ timeValue: d.timeValue, lot: d.lot });
        }
      }

      const END_SESSION = timeToSecondsRelative('16:30:00');
      const openingTrade = doneParsed.find(d => d.timeValue >= 0);
      const openingPrice = openingTrade ? openingTrade.price : -1;

      const orderMap = new Map<string, any>();
      for (const row of parsed) {
        if (!orderMap.has(row.orderNo)) {
          orderMap.set(row.orderNo, { ...row, initialLot: row.release });
        } else {
          const existing = orderMap.get(row.orderNo);
          if (row.timeValue < existing.timeValue) existing.timeValue = row.timeValue;
          if (row.message !== 'Open') existing.message = row.message;
          if (row.release > existing.initialLot) {
            existing.initialLot = row.release;
          }
        }
      }

      const syntheticEvents: any[] = [];
      let eventId = 0;
      const formatTime = (secs: number) => {
        let totalSeconds = Math.round(secs) + 32400;
        if (totalSeconds < 0) totalSeconds += 86400;
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const sc = totalSeconds % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sc).padStart(2, '0')}`;
      };

      for (const order of orderMap.values()) {
        const orderTime = order.timeValue;
        if (orderTime < -120) {
          if (order.price === openingPrice) {
            continue;
          }
        }
        let remainingLot = order.initialLot;
        const isWithdrawn = remainingLot > 0 && order.message !== 'Open';
        let debugBroker = order.broker;
        if (isWithdrawn) {
          let deadline = END_SESSION;
          let bestDeadline = Infinity;
          const targetOrderNo = Number(order.orderNo);

          // Case 1: trade at a price that makes this order impossible to still be live
          for (const [p, trades] of doneTradesByPrice.entries()) {
            const violates = order.buySell === 'B' ? p < order.price : p > order.price;
            if (violates) {
              const firstAfter = trades.find((d: any) => d.timeValue > orderTime);
              if (firstAfter && firstAfter.timeValue < bestDeadline) {
                bestDeadline = firstAfter.timeValue;
                deadline = firstAfter.timeValue;
              }
            }
          }
          // Case 2: trade at the SAME price by a NEWER (higher) order number
          const samePriceTrades = doneTradesByPrice.get(order.price);
          if (samePriceTrades) {
            for (const d of samePriceTrades) {
              if (d.timeValue <= orderTime) continue;
              const doneOrderNo = order.buySell === 'B' ? Number(d.buyerOrderNo) : Number(d.sellerOrderNo);
              if (!isNaN(doneOrderNo) && doneOrderNo > 0 && doneOrderNo > targetOrderNo) {
                if (d.timeValue < bestDeadline) {
                  bestDeadline = d.timeValue;
                  deadline = d.timeValue;
                }
                break;
              }
            }
          }
          const removalTime = Math.round(orderTime + 0.5 * (deadline - orderTime));
          const h = Math.floor(removalTime / 3600).toString().padStart(2, '0');
          const m = Math.floor((removalTime % 3600) / 60).toString().padStart(2, '0');
          debugBroker = `${order.broker} W${h}${m}`;
        }

        syntheticEvents.push({
          ...order,
          id: `syn_open_${eventId++}`,
          timeValue: orderTime,
          time: formatTime(orderTime),
          message: 'Open',
          open: remainingLot,
          broker: debugBroker
        });

        const matches = doneByOrder.get(order.orderNo) || [];
        matches.sort((a, b) => a.timeValue - b.timeValue);
        let lastMatchTime = orderTime;
        for (const match of matches) {
          if (remainingLot <= 0) break;
          const matchLot = Math.min(match.lot, remainingLot);
          remainingLot -= matchLot;
          let matchTime = Math.round(Math.max(orderTime, match.timeValue));
          if (matchTime < 0) matchTime = -1;
          lastMatchTime = Math.max(lastMatchTime, matchTime);
          syntheticEvents.push({
            ...order,
            id: `syn_match_${eventId++}`,
            timeValue: matchTime,
            time: formatTime(matchTime),
            message: remainingLot > 0 ? 'Partial Matched' : 'Full Matched',
            open: remainingLot,
            lot: matchLot
          });
        }

        if (remainingLot > 0) {
          let deadline = END_SESSION;
          let bestDeadline = Infinity;
          const targetOrderNo = Number(order.orderNo);

          // Case 1: trade at a price that makes this order impossible to still be live
          for (const [p, trades] of doneTradesByPrice.entries()) {
            const violates = order.buySell === 'B' ? p < order.price : p > order.price;
            if (violates) {
              const firstAfter = trades.find((d: any) => d.timeValue > orderTime);
              if (firstAfter && firstAfter.timeValue < bestDeadline) {
                bestDeadline = firstAfter.timeValue;
                deadline = firstAfter.timeValue;
              }
            }
          }
          // Case 2: trade at the SAME price by a NEWER (higher) order number
          const samePriceTrades2 = doneTradesByPrice.get(order.price);
          if (samePriceTrades2) {
            for (const d of samePriceTrades2) {
              if (d.timeValue <= orderTime) continue;
              const doneOrderNo = order.buySell === 'B' ? Number(d.buyerOrderNo) : Number(d.sellerOrderNo);
              if (!isNaN(doneOrderNo) && doneOrderNo > 0 && doneOrderNo > targetOrderNo) {
                if (d.timeValue < bestDeadline) {
                  bestDeadline = d.timeValue;
                  deadline = d.timeValue;
                }
                break;
              }
            }
          }
          let removalTime = Math.round(orderTime + 0.5 * (deadline - orderTime));
          if (removalTime < lastMatchTime) {
            removalTime = lastMatchTime;
          }
          if (removalTime < 0) removalTime = -1;
          syntheticEvents.push({
            ...order,
            id: `syn_withdrawn_${eventId++}`,
            timeValue: removalTime,
            time: formatTime(removalTime),
            message: (() => {
              if (order.message === 'Open') return 'Withdrawn';
              if (order.message === 'Partial Matched') return 'Withdrawn Match';
              return order.message;
            })(),
            open: 0,
            lot: remainingLot
          });
        }
      }

      syntheticEvents.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        const aOrd = Number(a.orderNo) || Infinity;
        const bOrd = Number(b.orderNo) || Infinity;
        return aOrd - bOrd;
      });

      // ==========================================
      // GHOST ORDER AUTO-CLEANING (CROSSED BOOK FIX)
      // ==========================================
      const timeline: any[] = [];
      for (const ev of syntheticEvents) {
        timeline.push({ type: 'order', timeValue: ev.timeValue, data: ev });
      }
      for (const tx of doneParsed) {
        timeline.push({ type: 'trade', timeValue: tx.timeValue, data: tx });
      }
      timeline.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        // Prioritize trades over orders at the exact same second to update lastTradePrice first
        if (a.type === 'trade' && b.type === 'order') return -1;
        if (a.type === 'order' && b.type === 'trade') return 1;
        return 0;
      });

      const activeBids = new Map<number, number>();
      const activeOffers = new Map<number, number>();
      let lastTradePrice = openingPrice > 0 ? openingPrice : -1;
      const earlyWithdrawals = new Map<string, number>();

      for (const item of timeline) {
        if (item.type === 'trade') {
          const tradePrice = item.data.price;
          lastTradePrice = tradePrice;

          // Trade-Based Ghost Order Elimination
          // If a trade occurs at P, any Offer < P or Bid > P must be a ghost order
          // Window: regular + post-close s/d 16:30 (27000)
          if (item.timeValue > 0 && item.timeValue < 27000) {
            for (const [oNo, p] of activeOffers.entries()) {
              if (p < tradePrice) {
                earlyWithdrawals.set(String(oNo), item.timeValue);
                activeOffers.delete(oNo);
              }
            }
            for (const [oNo, p] of activeBids.entries()) {
              if (p > tradePrice) {
                earlyWithdrawals.set(String(oNo), item.timeValue);
                activeBids.delete(oNo);
              }
            }
          }
        } else {
          const ev = item.data;
          const orderNo = Number(ev.orderNo);
          if (ev.message === 'Open' || ev.message === 'Partial Matched') {
            if (ev.buySell === 'B') activeBids.set(orderNo, ev.price);
            else activeOffers.set(orderNo, ev.price);
          } else { // Full Matched, Withdrawn, etc.
            activeBids.delete(orderNo);
            activeOffers.delete(orderNo);
          }

          if (ev.open <= 0) {
            activeBids.delete(orderNo);
            activeOffers.delete(orderNo);
          }

          // Check for crossed book (09:00 - 16:30)
          // timeValue 0 is 09:00:00, timeValue 27000 is 16:30:00
          if (item.timeValue > 0 && item.timeValue < 27000) {
            let crossFound = true;
            while (crossFound) {
              crossFound = false;
              let bestBid = -1;
              let bestBidOrderNo = -1;
              for (const [oNo, p] of activeBids.entries()) {
                if (p > bestBid || (p === bestBid && oNo < bestBidOrderNo)) {
                  bestBid = p;
                  bestBidOrderNo = oNo;
                }
              }

              let bestOffer = Infinity;
              let bestOfferOrderNo = -1;
              for (const [oNo, p] of activeOffers.entries()) {
                if (p < bestOffer || (p === bestOffer && oNo < bestOfferOrderNo)) {
                  bestOffer = p;
                  bestOfferOrderNo = oNo;
                }
              }

              if (bestBid !== -1 && bestOffer !== Infinity && bestOffer < bestBid) {
                crossFound = true;
                let orderToWithdraw = -1;

                if (lastTradePrice !== -1) {
                  if (lastTradePrice >= bestBid) {
                    orderToWithdraw = bestOfferOrderNo;
                  } else if (lastTradePrice <= bestOffer) {
                    orderToWithdraw = bestBidOrderNo;
                  } else {
                    orderToWithdraw = Math.min(bestBidOrderNo, bestOfferOrderNo);
                  }
                } else {
                  orderToWithdraw = Math.min(bestBidOrderNo, bestOfferOrderNo);
                }

                if (orderToWithdraw !== -1) {
                  earlyWithdrawals.set(String(orderToWithdraw), item.timeValue);
                  activeBids.delete(orderToWithdraw);
                  activeOffers.delete(orderToWithdraw);
                }
              }
            }
          }
        }
      }

      // Apply early withdrawals and clean up syntheticEvents
      const cleanedSyntheticEvents: any[] = [];
      const orderLastLot = new Map<string, number>();
      const orderCurrentMsg = new Map<string, string>();
      const forceWithdrawnSet = new Set<string>();

      for (const ev of syntheticEvents) {
        const orderNo = String(ev.orderNo);
        const earlyWithdrawTime = earlyWithdrawals.get(orderNo);

        if (earlyWithdrawTime !== undefined) {
          if (ev.timeValue > earlyWithdrawTime) {
            if (!forceWithdrawnSet.has(orderNo) && orderLastLot.has(orderNo) && orderLastLot.get(orderNo)! > 0) {
              const lastMsg = orderCurrentMsg.get(orderNo) || 'Open';
              cleanedSyntheticEvents.push({
                ...ev,
                id: `syn_forced_withdrawn_${eventId++}`,
                timeValue: earlyWithdrawTime,
                time: formatTime(earlyWithdrawTime),
                message: lastMsg !== 'Open' ? lastMsg : 'Withdrawn',
                open: 0,
                lot: orderLastLot.get(orderNo)
              });
              orderLastLot.set(orderNo, 0);
              forceWithdrawnSet.add(orderNo);
            }
            continue; // Skip any events after early withdrawal time
          }
        }

        cleanedSyntheticEvents.push(ev);
        if (ev.open !== undefined) {
          orderLastLot.set(orderNo, ev.open);
        }
        if (ev.message) {
          orderCurrentMsg.set(orderNo, ev.message);
        }
      }

      for (const [orderNo, earlyWithdrawTime] of earlyWithdrawals.entries()) {
        if (!forceWithdrawnSet.has(orderNo) && orderLastLot.has(orderNo) && orderLastLot.get(orderNo)! > 0) {
          const templateEv = syntheticEvents.find(e => String(e.orderNo) === orderNo);
          if (templateEv) {
            const lastMsg = orderCurrentMsg.get(orderNo) || 'Open';
            cleanedSyntheticEvents.push({
              ...templateEv,
              id: `syn_forced_withdrawn_end_${eventId++}`,
              timeValue: earlyWithdrawTime,
              time: formatTime(earlyWithdrawTime),
              message: lastMsg !== 'Open' ? lastMsg : 'Withdrawn',
              open: 0,
              lot: orderLastLot.get(orderNo)
            });
            orderLastLot.set(orderNo, 0);
            forceWithdrawnSet.add(orderNo);
          }
        }
      }

      cleanedSyntheticEvents.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        const aOrd = Number(a.orderNo) || Infinity;
        const bOrd = Number(b.orderNo) || Infinity;
        return aOrd - bOrd;
      });

      if (!isMountedRef.current || signal.aborted) return;
      setRealOrders(cleanedSyntheticEvents);
      setRealDoneOrders(doneParsed);
      setIsFullDataLoaded(true);
      console.log('[OrderbookReplay] Main thread fallback completed successfully.');

    } catch (e: any) {
      console.error('[OrderbookReplay] Main thread fallback parsing failed:', e);
      setIsFullDataLoaded(true);
    }
  };

  const handleShow = async () => {
    console.log('[OrderbookReplay] 🎬 handleShow() called');
    console.log('[OrderbookReplay] Stock:', stockInput, '| Date:', targetDate, '| Time:', startTime, '-', endTime);
    setIsLoading(true);
    setRealOrders([]);
    setRealDoneOrders([]);
    setBuyFilter(EMPTY_FILTER);
    setBuyApplied(EMPTY_FILTER);
    setSellFilter(EMPTY_FILTER);
    setSellApplied(EMPTY_FILTER);
    setTradeFilter(EMPTY_FILTER);
    setTradeApplied(EMPTY_FILTER);
    setCurrentTime(0);
    try {
      console.log('[OrderbookReplay] 1️⃣ Fetching SAS token from backend...');
      const tokenRes = await api.getOrderbookReplaySasToken();
      console.log('[OrderbookReplay] SAS token response received:', tokenRes?.success === true);

      if (!isMountedRef.current) return;

      if (!tokenRes.success || !tokenRes.data) {
        throw new Error(tokenRes.error || 'Failed to authorize access.');
      }

      const { containerUrl, sasToken, expiresAt } = tokenRes.data;
      console.log('[OrderbookReplay] ✅ SAS token received. containerUrl:', containerUrl, '| expires:', expiresAt);

      const formattedStock = stockInput.trim().toUpperCase();
      console.log('[OrderbookReplay] 2️⃣ Loading data for:', formattedStock, 'on', targetDate);

      // 1. Construct and resolve bo_bundle ZIP paths (supports current format with OHLC in filename & legacy format)
      const dateFormatted = targetDate.replace(/-/g, '');

      // Reset abort controller for any previous downloads/workers
      if (backgroundLoaderAbortControllerRef.current) {
        backgroundLoaderAbortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      backgroundLoaderAbortControllerRef.current = abortController;

      console.log('[OrderbookReplay] Resolving ZIP bundle paths...');
      const { part1ZipUrl, fullZipUrl, parsedOhlc } = await resolveBoBundleUrls(
        containerUrl,
        sasToken,
        dateFormatted,
        formattedStock,
        abortController.signal
      );

      // Start downloading part 1 and full ZIP concurrently
      console.log('[OrderbookReplay] Initiating concurrent ZIP downloads...', { part1ZipUrl, fullZipUrl });
      const part1Promise = part1ZipUrl
        ? Promise.all([
            fetchZipEntry(part1ZipUrl, 'order_detail.parquet', abortController.signal).catch(() => null),
            fetchZipEntry(part1ZipUrl, 'done_detail.parquet', abortController.signal).catch(() => null),
            fetchZipEntry(part1ZipUrl, 'metadata.json', abortController.signal).catch(() => null),
          ])
        : Promise.resolve([null, null, null] as [null, null, null]);

      const fullPromise = fullZipUrl
        ? Promise.all([
            fetchZipEntry(fullZipUrl, 'order_detail.parquet', abortController.signal).catch(() => null),
            fetchZipEntry(fullZipUrl, 'done_detail.parquet', abortController.signal).catch(() => null),
            fetchZipEntry(fullZipUrl, 'metadata.json', abortController.signal).catch(() => null),
          ])
        : Promise.resolve([null, null, null] as [null, null, null]);

      let usedPart1 = false;
      let orderBytes: Uint8Array | null = null;
      let doneBytes: Uint8Array | null = null;
      let metadataBytes: Uint8Array | null = null;

      // Try loading part1 ZIP first
      try {
        console.log('[OrderbookReplay] Awaiting part1 ZIP...');
        const [ord, done, meta] = await part1Promise;
        if (ord) {
          orderBytes = ord;
          doneBytes = done;
          metadataBytes = meta;
          usedPart1 = true;
          console.log('[OrderbookReplay] Successfully loaded from part1 ZIP');
        }
      } catch (err) {
        console.warn('[OrderbookReplay] Failed to load part1 ZIP:', err);
      }

      // If part1 ZIP failed/not found, await full ZIP (which we already started downloading!)
      if (!orderBytes) {
        try {
          console.log('[OrderbookReplay] Part1 ZIP failed or empty, awaiting full ZIP...');
          const [ord, done, meta] = await fullPromise;
          if (ord) {
            orderBytes = ord;
            doneBytes = done;
            metadataBytes = meta;
            console.log('[OrderbookReplay] Successfully loaded from full ZIP');
          }
        } catch (err) {
          console.warn('[OrderbookReplay] Failed to load full ZIP:', err);
        }
      }

      let rows: any[] = [];
      let doneRows: any[] = [];
      let parsedMetadata: any = null;

      if (orderBytes) {
        // Parse from ZIP buffers
        rows = await parseOrderDetails(orderBytes.buffer);
        if (doneBytes) {
          doneRows = await parseDoneDetails(doneBytes.buffer);
        }
        if (metadataBytes) {
          try {
            parsedMetadata = JSON.parse(new TextDecoder().decode(metadataBytes));
            console.log('[OrderbookReplay] Parsed metadata.json from ZIP:', parsedMetadata);
          } catch (e) {
            console.warn('[OrderbookReplay] Failed to parse metadata.json:', e);
          }
        }
      } else {
        // Fallback: fetch individual parquet files
        console.log('[OrderbookReplay] Fallback to individual Parquet files...');
        const orderDetailsPromise = loadOrderDetails(formattedStock, targetDate, containerUrl, sasToken);
        const doneDetailsPromise = loadDoneDetails(formattedStock, targetDate, containerUrl, sasToken).catch(err => {
          console.warn('[OrderbookReplay] Failed to load done details:', err);
          showToast({ type: 'error', title: 'Data Error', message: `Gagal memuat Done Detail: ${err.message}` });
          return [] as any[];
        });
        [rows, doneRows] = await Promise.all([orderDetailsPromise, doneDetailsPromise]);
      }

      if (!isMountedRef.current) return;

      // Build order_no → broker map per side so we can look up counter-broker and fill missing brokers
      // Key: `${order_no}_B` or `${order_no}_S` → broker code
      const orderBrokerMap: Record<string, string> = {};
      for (const row of rows) {
        const side = row.bs || 'B';
        const oNum = String(Number(row.order_no) || row.order_no).trim();
        const brk = String(row.brk || '').trim();
        if (brk && brk !== '--' && oNum) {
          orderBrokerMap[`${oNum}_${side}`] = brk;
        }
      }
      for (const d of doneRows) {
        const o1 = String(Number(d.TRX_ORD1) || d.TRX_ORD1 || '').trim();
        const b1 = String(d.BRK_COD1 || '').trim();
        if (b1 && b1 !== '--' && o1) orderBrokerMap[`${o1}_B`] = b1;

        const o2 = String(Number(d.TRX_ORD2) || d.TRX_ORD2 || '').trim();
        const b2 = String(d.BRK_COD2 || '').trim();
        if (b2 && b2 !== '--' && o2) orderBrokerMap[`${o2}_S`] = b2;
      }

      const parsed = rows.map((row, index) => {
        const timeOffset = timeToSecondsRelative(row.time);
        const side = (row.bs || 'B') as 'B' | 'S';
        const orderNoNum = String(Number(row.order_no) || row.order_no).trim();
        let broker = String(row.brk || '').trim();

        if (!broker || broker === '--') {
          broker = orderBrokerMap[`${orderNoNum}_${side}`] || '--';
        }

        const counterSide = side === 'B' ? 'S' : 'B';
        const counterBroker = orderBrokerMap[`${orderNoNum}_${counterSide}`] || '--';
        // Lot traded = rls (released/matched qty); opn = remaining open qty
        const lotTraded = Number(row.rls) || 0;

        return {
          id: row.queue || index,
          queue: row.queue,
          orderNo: orderNoNum,
          time: row.time,
          timeValue: timeOffset,
          stockCode: row.stock,
          buySell: side,
          board: row.board,
          price: Number(row.price),
          open: Number(row.opn),
          lot: lotTraded,
          release: Number(row.rls),
          foreignDomestic: String(row.inv || '').trim() === 'F' ? 'F' : 'D',
          brokerCode: broker,
          message: row.message,
          buyerBroker: side === 'B' ? broker : counterBroker,
          sellerBroker: side === 'S' ? broker : counterBroker,
        };
      });

      // Sort parsed orders chronologically by timeValue first, then by orderNo
      parsed.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        const aOrd = Number(a.orderNo) || Infinity;
        const bOrd = Number(b.orderNo) || Infinity;
        return aOrd - bOrd;
      });

      console.log('[OrderbookReplay] 4️⃣ Parsed and sorted', parsed.length, 'orders. Sample:', parsed.slice(0, 2));

      setRealOrders(parsed);
      setAppliedStock(formattedStock);

      // Load done_detail for TradeHistory and Event simulation
      console.log('[OrderbookReplay] Loading done details...');
      if (doneRows.length > 0) {
        console.log('[OrderbookReplay] First doneRow:', doneRows[0]);
      }
      let doneParsed: any[] = doneRows.map((row, index) => {
        let timeStr = String(row.TRX_TIME || '');
        if (timeStr.length === 5) timeStr = `0${timeStr}`; // e.g. 90001 -> 090001
        const formattedTime = timeStr.length === 6 ? `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}` : timeStr;
        const timeOffset = timeToSecondsRelative(formattedTime);

        const isBuy = row.HAKA_HAKI === 1 || row.HAKA_HAKI === '1';
        const bs = isBuy ? 'B' : 'S';

        return {
          id: `done_${index}`,
          time: formattedTime,
          timeValue: timeOffset,
          stockCode: formattedStock,
          buySell: bs,
          board: row.TRX_TYPE || row.BOARD || 'RG',
          price: Number(row.STK_PRIC),
          lot: (Number(row.STK_VOLM) || 0) / 100, // lot is volume/100
          foreignDomestic: ['F', 'A'].includes(String(row.INV_TYP1 || '').trim().toUpperCase()) ? 'F' : 'D',
          foreignDomesticBuyer: ['F', 'A'].includes(String(row.INV_TYP1 || '').trim().toUpperCase()) ? 'F' : 'D',
          foreignDomesticSeller: ['F', 'A'].includes(String(row.INV_TYP2 || '').trim().toUpperCase()) ? 'F' : 'D',
          buyerBroker: String(row.BRK_COD1 || '--'),
          sellerBroker: String(row.BRK_COD2 || '--'),
          buyerOrderNo: String(Number(row.TRX_ORD1) || row.TRX_ORD1 || ''),
          sellerOrderNo: String(Number(row.TRX_ORD2) || row.TRX_ORD2 || ''),
          message: 'Full Matched',
          brokerCode: String(row.BRK_COD1 || '--'),
        };
      });
      doneParsed.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        const aMinOrd = Math.min(Number(a.buyerOrderNo) || Infinity, Number(a.sellerOrderNo) || Infinity);
        const bMinOrd = Math.min(Number(b.buyerOrderNo) || Infinity, Number(b.sellerOrderNo) || Infinity);
        return aMinOrd - bMinOrd;
      });
      setRealDoneOrders(doneParsed);

      // --- SYNTHETIC EVENTS GENERATION ---
      const doneTradesByPrice = new Map<number, any[]>();
      const doneByOrder = new Map<string, any[]>();

      for (const d of doneParsed) {
        if (!doneTradesByPrice.has(d.price)) doneTradesByPrice.set(d.price, []);
        doneTradesByPrice.get(d.price)!.push(d);

        if (d.buyerOrderNo) {
          if (!doneByOrder.has(d.buyerOrderNo)) doneByOrder.set(d.buyerOrderNo, []);
          doneByOrder.get(d.buyerOrderNo)!.push({ timeValue: d.timeValue, lot: d.lot });
        }
        if (d.sellerOrderNo) {
          if (!doneByOrder.has(d.sellerOrderNo)) doneByOrder.set(d.sellerOrderNo, []);
          doneByOrder.get(d.sellerOrderNo)!.push({ timeValue: d.timeValue, lot: d.lot });
        }
      }

      // Pre-opening params
      const END_SESSION = timeToSecondsRelative('16:30:00');
      const openingTrade = doneParsed.find(d => d.timeValue >= 0);
      const openingPrice = openingTrade ? openingTrade.price : -1;

      // Group orders
      const orderMap = new Map<string, any>();
      for (const row of parsed) {
        if (!orderMap.has(row.orderNo)) {
          orderMap.set(row.orderNo, { ...row, initialLot: row.release });
        } else {
          const existing = orderMap.get(row.orderNo);
          if (row.timeValue < existing.timeValue) existing.timeValue = row.timeValue;
          if (row.message !== 'Open') existing.message = row.message;
          if (row.release > existing.initialLot) {
            existing.initialLot = row.release;
          }
        }
      }

      const syntheticEvents: any[] = [];
      let eventId = 0;

      const formatTime = (secs: number) => {
        let totalSeconds = Math.round(secs) + 32400;
        if (totalSeconds < 0) totalSeconds += 86400; // Wrap around if before midnight
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const sc = totalSeconds % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sc).padStart(2, '0')}`;
      };

      const findFirstIndexAfterTime = (trades: any[], timeLimit: number): number => {
        let low = 0;
        let high = trades.length - 1;
        let ans = trades.length;
        while (low <= high) {
          const mid = (low + high) >> 1;
          if (trades[mid].timeValue > timeLimit) {
            ans = mid;
            high = mid - 1;
          } else {
            low = mid + 1;
          }
        }
        return ans;
      };

      for (const order of orderMap.values()) {
        const orderTime = order.timeValue;

        if (orderTime < -120) { // < 08:58:00 (which is -120s from 09:00:00)
          if (order.price === openingPrice) {
            continue; // Pre-opening rule: ignore
          }
        }

        let remainingLot = order.initialLot;

        const isWithdrawn = remainingLot > 0 && order.message !== 'Open';
        let debugBroker = order.broker;

        if (isWithdrawn) {
          let removalTime = END_SESSION;
          let diffPriceTx: any = undefined;
          let bestDeadline = Infinity;

          const targetOrderNo = Number(order.orderNo);

          if (order.buySell === 'B') {
            for (const [p, trades] of doneTradesByPrice.entries()) {
              if (p < order.price) {
                const startIdx = findFirstIndexAfterTime(trades, orderTime);
                for (let i = startIdx; i < trades.length; i++) {
                  const d = trades[i];
                  if (d.board !== 'RG') continue;
                  if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
            const samePriceTrades = doneTradesByPrice.get(order.price);
            if (samePriceTrades) {
              const idx = findFirstIndexAfterTime(samePriceTrades, orderTime);
              for (let i = idx; i < samePriceTrades.length; i++) {
                const d = samePriceTrades[i];
                if (d.board !== 'RG') continue;
                if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                const doneOrderNo = Number(d.buyerOrderNo);
                if (!isNaN(doneOrderNo) && doneOrderNo > 0 && doneOrderNo > targetOrderNo) {
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
          } else {
            for (const [p, trades] of doneTradesByPrice.entries()) {
              if (p > order.price) {
                const startIdx = findFirstIndexAfterTime(trades, orderTime);
                for (let i = startIdx; i < trades.length; i++) {
                  const d = trades[i];
                  if (d.board !== 'RG') continue;
                  if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
            const samePriceTrades = doneTradesByPrice.get(order.price);
            if (samePriceTrades) {
              const idx = findFirstIndexAfterTime(samePriceTrades, orderTime);
              for (let i = idx; i < samePriceTrades.length; i++) {
                const d = samePriceTrades[i];
                if (d.board !== 'RG') continue;
                if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                const doneOrderNo = Number(d.sellerOrderNo);
                if (!isNaN(doneOrderNo) && doneOrderNo > 0 && doneOrderNo > targetOrderNo) {
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
          }

          if (diffPriceTx) {
            const deadline = diffPriceTx.timeValue;
            removalTime = Math.round(orderTime + 0.5 * (deadline - orderTime));
          }

          const h = Math.floor(removalTime / 3600).toString().padStart(2, '0');
          const m = Math.floor((removalTime % 3600) / 60).toString().padStart(2, '0');
          debugBroker = `${order.broker} W${h}${m}`;
        }

        syntheticEvents.push({
          ...order,
          id: `syn_open_${eventId++}`,
          timeValue: orderTime,
          time: formatTime(orderTime),
          message: 'Open',
          open: remainingLot,
          broker: debugBroker
        });

        const matches = doneByOrder.get(order.orderNo) || [];
        matches.sort((a, b) => a.timeValue - b.timeValue);

        let lastMatchTime = orderTime;
        for (const match of matches) {
          if (remainingLot <= 0) break;
          const matchLot = Math.min(match.lot, remainingLot);
          remainingLot -= matchLot;

          let matchTime = Math.round(Math.max(orderTime, match.timeValue));
          if (matchTime < 0) matchTime = -1; // Freeze pre-market matches until 08:59:59
          lastMatchTime = Math.max(lastMatchTime, matchTime);

          syntheticEvents.push({
            ...order,
            id: `syn_match_${eventId++}`,
            timeValue: matchTime,
            time: formatTime(matchTime),
            message: remainingLot > 0 ? 'Partial Matched' : 'Full Matched',
            open: remainingLot,
            lot: matchLot
          });
        }

        if (remainingLot > 0) {
          let removalTime = END_SESSION;

          let diffPriceTx: any = undefined;
          let bestDeadline = Infinity;

          const targetOrderNo = Number(order.orderNo);

          if (order.buySell === 'B') {
            for (const [p, trades] of doneTradesByPrice.entries()) {
              if (p < order.price) {
                const startIdx = findFirstIndexAfterTime(trades, orderTime);
                for (let i = startIdx; i < trades.length; i++) {
                  const d = trades[i];
                  if (d.board !== 'RG') continue;
                  if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
            const samePriceTrades = doneTradesByPrice.get(order.price);
            if (samePriceTrades) {
              const idx = findFirstIndexAfterTime(samePriceTrades, orderTime);
              for (let i = idx; i < samePriceTrades.length; i++) {
                const d = samePriceTrades[i];
                if (d.board !== 'RG') continue;
                if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                const doneOrderNo = Number(d.buyerOrderNo);
                if (!isNaN(doneOrderNo) && doneOrderNo > 0 && doneOrderNo > targetOrderNo) {
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
          } else {
            for (const [p, trades] of doneTradesByPrice.entries()) {
              if (p > order.price) {
                const startIdx = findFirstIndexAfterTime(trades, orderTime);
                for (let i = startIdx; i < trades.length; i++) {
                  const d = trades[i];
                  if (d.board !== 'RG') continue;
                  if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
            const samePriceTrades = doneTradesByPrice.get(order.price);
            if (samePriceTrades) {
              const idx = findFirstIndexAfterTime(samePriceTrades, orderTime);
              for (let i = idx; i < samePriceTrades.length; i++) {
                const d = samePriceTrades[i];
                if (d.board !== 'RG') continue;
                if (d.time === '16:00:00' || d.time === '09:00:00') continue;
                const doneOrderNo = Number(d.sellerOrderNo);
                if (!isNaN(doneOrderNo) && doneOrderNo > 0 && doneOrderNo > targetOrderNo) {
                  if (d.timeValue < bestDeadline) {
                    bestDeadline = d.timeValue;
                    diffPriceTx = d;
                  }
                  break;
                }
              }
            }
          }

          if (diffPriceTx) {
            const deadline = diffPriceTx.timeValue;
            removalTime = Math.round(orderTime + 0.5 * (deadline - orderTime));

            if (removalTime < lastMatchTime) {
              removalTime = lastMatchTime;
            }

            if (removalTime < 0) removalTime = -1; // Freeze pre-market withdrawals until 08:59:59
          }

          syntheticEvents.push({
            ...order,
            id: `syn_withdrawn_${eventId++}`,
            timeValue: removalTime,
            time: formatTime(removalTime),
            message: order.message !== 'Open' ? order.message : (removalTime === END_SESSION ? 'Expired' : 'Withdrawn'),
            open: 0,
            lot: remainingLot
          });
        }
      }

      syntheticEvents.sort((a, b) => {
        if (a.timeValue !== b.timeValue) return a.timeValue - b.timeValue;
        const aOrd = Number(a.orderNo) || Infinity;
        const bOrd = Number(b.orderNo) || Infinity;
        return aOrd - bOrd;
      });

      console.log('[OrderbookReplay] 4️⃣ Generated', syntheticEvents.length, 'synthetic events. Sample:', syntheticEvents.slice(0, 2));

      setRealOrders(syntheticEvents);
      setAppliedStock(formattedStock);

      let startOffset = 0;
      let endOffset = 7.5 * 3600;

      if (parsed.length > 0 || doneParsed.length > 0) {
        const earliestOrderTime = parsed.length > 0 ? parsed[0].timeValue : Infinity;
        const earliestDoneTime = doneParsed.length > 0 ? doneParsed[0].timeValue : Infinity;
        startOffset = Math.min(earliestOrderTime, earliestDoneTime);
        if (startOffset === Infinity) startOffset = 0;

        if (parsed.length > 0) {
          const earliestOrder = parsed[0];
          if (earliestOrder.time) {
            // Force the UI input box to show 09:00:00 to keep it consistent
            setStartTime("09:00:00");
          }
          if (usedPart1) {
            endOffset = timeToSecondsRelative(endTime);
          } else {
            const latestOrder = parsed[parsed.length - 1];
            const latestDone = doneParsed.length > 0 ? doneParsed[doneParsed.length - 1] : null;
            // Running trade bisa jalan s/d 16:30 (done detail ada yang lewat 15:59),
            // jadi ambil max dari order_detail & done_detail.
            endOffset = latestOrder.timeValue;
            if (latestDone && latestDone.timeValue > endOffset) endOffset = latestDone.timeValue;
            let timeStr = '';
            if (latestDone && latestDone.timeValue >= endOffset) {
              timeStr = latestDone.time ? latestDone.time.substring(0, 8) : '';
            } else {
              timeStr = latestOrder.time ? latestOrder.time.substring(0, 8) : '';
            }

            const maxEndOffset = timeToSecondsRelative('16:30:00');
            if (endOffset > maxEndOffset) {
              endOffset = maxEndOffset;
              timeStr = '16:30:00';
            }
            if (timeStr) setEndTime(timeStr);
          }
        } else if (doneParsed.length > 0) {
          // Tidak ada order_detail tapi ada done (mis. replay TN): pakai done terakhir.
          const latestDone = doneParsed[doneParsed.length - 1];
          endOffset = latestDone.timeValue;
          const maxEndOffset = timeToSecondsRelative('16:30:00');
          if (endOffset > maxEndOffset) endOffset = maxEndOffset;
          if (latestDone.time) setEndTime(latestDone.time.substring(0, 8));
        }
      } else {
        startOffset = timeToSecondsRelative(startTime);
        endOffset = timeToSecondsRelative(endTime);
      }

      // Start slider at 08:59:59 (-1) so we see pre-opening trades but don't leak 09:00:00 trades yet.
      // If startOffset is already >= 0, we can just use startOffset.
      const openTime = Math.max(startOffset, -1);

      setAppliedStartSec(startOffset);
      const HARD_MAX_END = timeToSecondsRelative('16:30:00');
      const clampedEnd = Math.min(endOffset > startOffset ? endOffset : 7.5 * 3600, HARD_MAX_END);
      setAppliedEndSec(clampedEnd);
      setAppliedBoard(selectedBoard);
      setAppliedFD(selectedFD);
      setAppliedShowBroker(showBroker);
      setAppliedShowOrderNo(showOrderNo);

      setIsPlaying(false);
      setCurrentTime(openTime);

      // Handle background download if part1 ZIP was used
      if (usedPart1) {
        setIsFullDataLoaded(false);
        triggerBackgroundLoad(formattedStock, fullPromise, abortController.signal);
      } else {
        setIsFullDataLoaded(true);
      }

      // Load OHLC for previous-close (best-effort; doesn't block the replay)
      setPrevClose(null);
      if (parsedMetadata && typeof parsedMetadata.prevClose === 'number') {
        setPrevClose(parsedMetadata.prevClose);
        console.log('[OrderbookReplay] Using prevClose from metadata.json:', parsedMetadata.prevClose);
      } else if (parsedOhlc && typeof parsedOhlc.prevClose === 'number') {
        setPrevClose(parsedOhlc.prevClose);
        console.log('[OrderbookReplay] Using prevClose from ZIP filename:', parsedOhlc.prevClose);
      } else {
        console.log('[OrderbookReplay] Fallback to OHLC Parquet for prevClose...');
        getOhlcBlobPath(formattedStock)
          .then(ohlcPath => (ohlcPath ? loadOhlcByPath(ohlcPath, containerUrl, sasToken) : null))
          .then(ohlcRows => {
            if (!isMountedRef.current) return;
            if (!ohlcRows) return;
            let prevRow: any = null;
            for (const r of ohlcRows) {
              const d = r.Date ?? r.date;
              if (d && d < targetDate && (!prevRow || d > (prevRow.Date ?? prevRow.date))) {
                prevRow = r;
              }
            }
            if (!prevRow) return;
            const pc = Number(prevRow.Close ?? prevRow.close ?? 0);
            if (pc > 0) setPrevClose(pc);
          })
          .catch(err => {
            console.warn('[OrderbookReplay] Failed to load OHLC fallback:', err);
          });
      }
    } catch (err: any) {
      console.error('[OrderbookReplay] ❌ handleShow error:', err);
      setRealOrders([]);
      setRealDoneOrders([]);
    } finally {
      setIsLoading(false);
      console.log('[OrderbookReplay] handleShow() done');
    }
  };

  // Auto-trigger Show when returning to page with prior configuration.
  // Guarded with a ref (not just []) because React 18 StrictMode double-invokes
  // mount effects in dev, which would otherwise call handleShow() twice and
  // fire two parallel SAS-token + parquet fetches.
  const autoShowTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoShowTriggeredRef.current) return;
    if (persisted?.targetDate) {
      autoShowTriggeredRef.current = true;
      handleShow();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  // Filter orders based on stock code, board, F/D, and broker
  const filteredOrders = useMemo(() => {
    return realOrders.filter(row => {
      const stockMatch = row.stockCode === appliedStock;
      const boardMatch = appliedBoard === 'All' || row.board === appliedBoard;
      const fdMatch = appliedFD === 'All' || row.foreignDomestic === appliedFD;
      const brokerMatch = selectedBrokers.includes('ALL') ||
        selectedBrokers.includes(row.brokerCode) ||
        selectedBrokers.includes(row.buyerBroker) ||
        selectedBrokers.includes(row.sellerBroker);
      return stockMatch && boardMatch && fdMatch && brokerMatch;
    });
  }, [realOrders, appliedStock, appliedBoard, appliedFD, selectedBrokers]);

  const filteredDoneOrders = useMemo(() => {
    return realDoneOrders.filter(row => {
      const stockMatch = row.stockCode === appliedStock;
      const boardMatch = appliedBoard === 'All' || row.board === appliedBoard;
      // For done orders, check if either side matches the F/D filter (or both, depending on logic. Let's just match either side)
      const fdMatch = appliedFD === 'All' || row.foreignDomesticBuyer === appliedFD || row.foreignDomesticSeller === appliedFD;
      const brokerMatch = selectedBrokers.includes('ALL') ||
        selectedBrokers.includes(row.buyerBroker) ||
        selectedBrokers.includes(row.sellerBroker);
      return stockMatch && boardMatch && fdMatch && brokerMatch;
    });
  }, [realDoneOrders, appliedStock, appliedBoard, appliedFD, selectedBrokers]);

  // Keep sorted order times in ref for gap-skip during playback
  useEffect(() => {
    const times = Array.from(new Set(filteredOrders.map(o => o.timeValue))).sort((a, b) => a - b);
    orderTimesRef.current = times;
  }, [filteredOrders]);

  const applyPanelFilter = (orders: any[], f: PanelFilter) =>
    orders.filter(o => {
      if (f.price && String(o.price) !== f.price) return false;
      if (f.broker && !String(o.brokerCode).toLowerCase().includes(f.broker.toLowerCase())) return false;
      if (f.message) {
        if (f.message === 'Open') {
          if (o.message !== 'Open' && o.message !== 'Partial Matched') return false;
        } else {
          if (o.message !== f.message) return false;
        }
      }
      return true;
    });

  const isPanelFilterActive = (f: PanelFilter) => f.price !== '' || f.broker !== '' || f.message !== '';

  const isControlFilterActive = appliedFD !== 'All' || !selectedBrokers.includes('ALL');

  const tradeOrders = useMemo(() => applyPanelFilter(filteredDoneOrders, tradeApplied), [filteredDoneOrders, tradeApplied]);

  // Orderbook stop 15:59, trade lanjut 16:30.
  const orderbookTime = useMemo(() => Math.min(currentTime - 1, ORDERBOOK_FREEZE_SEC), [currentTime]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full h-full flex flex-col bg-[#0a0f20] relative">

      {/* ── Loading Bar (full data) ── */}
      {!isFullDataLoaded && (
        <div className="absolute top-0 left-0 right-0 z-[6000] overflow-hidden" style={{ height: '3px', background: 'rgba(59,130,246,0.15)' }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '-60%',
              height: '100%',
              width: '60%',
              background: 'linear-gradient(90deg, transparent 0%, #60a5fa 40%, #93c5fd 60%, transparent 100%)',
              animation: 'orderbook-shimmer 1.6s linear infinite',
              willChange: 'transform',
            }}
          />
          <style>{`
            @keyframes orderbook-shimmer {
              0%   { transform: translateX(0); }
              100% { transform: translateX(266.67%); }
            }
          `}</style>
        </div>
      )}


      {/* ── Control Panel ── */}
      <div className="bg-card border-b border-[#3a4252] px-2 sm:px-3 py-1.5 sticky top-0 z-[5000]">
        <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-2 w-full">

          {/* Ticker (hidden in dashboard mode) */}
          {!isDashboard && (
            <div className="flex items-center gap-1 sm:gap-2">
              <label className="text-[12px] sm:text-[13px] font-medium text-white whitespace-nowrap">Ticker:</label>
              <div className="relative" ref={dropdownRef}>
                <div className="w-[75px] sm:w-32 h-8 sm:h-9 px-1.5 sm:px-3 flex items-center justify-between bg-background border border-[#3a4252] rounded-md text-[13px] sm:text-sm text-white hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                  <Search className="w-3.5 h-3.5 text-muted-foreground mr-1 sm:mr-2 flex-shrink-0" />
                  <input
                    type="text"
                    className={`w-full bg-transparent text-[13px] sm:text-sm text-white focus:outline-none placeholder:text-muted-foreground/50 truncate ${propSelectedStock ? 'cursor-not-allowed opacity-80' : ''}`}
                    placeholder="Select ticker"
                    value={stockInput}
                    onChange={(e) => {
                      if (propSelectedStock) return;
                      setStockInput(e.target.value.toUpperCase());
                      setShowStockSuggestions(true);
                    }}
                    onFocus={() => {
                      if (!propSelectedStock) setShowStockSuggestions(true);
                    }}
                    readOnly={!!propSelectedStock}
                  />
                  {!propSelectedStock && (
                    <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
                  )}
                </div>
                {showStockSuggestions && (
                  <div id="stock-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-[60] max-h-96 w-full overflow-hidden flex flex-col">
                    {/* Search Input (Removed) */}
                    <div className="overflow-y-auto flex-1">
                      {stockInput === '' ? (
                        <>
                          <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                            Available Stocks ({STOCK_LIST.length})
                          </div>
                          {STOCK_LIST.slice(0, 20).map((stock) => {
                            const isSelected = stock === stockInput;
                            return (
                              <div
                                key={stock}
                                onClick={() => { setStockInput(stock); }}
                                className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'hover:bg-muted'}`}
                              >
                                <span>{stock}</span>
                                {isSelected && <span className="text-primary text-xs">✓</span>}
                              </div>
                            );
                          })}
                          {STOCK_LIST.length > 20 && (
                            <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-t border-[#3a4252]">
                              ... and {STOCK_LIST.length - 20} more stocks
                            </div>
                          )}
                        </>
                      ) : filteredStocks.length > 0 ? (
                        <>
                          <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                            {filteredStocks.length} stocks found
                          </div>
                          {filteredStocks.slice(0, 20).map((stock) => {
                            const isSelected = stock === stockInput;
                            return (
                              <div
                                key={stock}
                                onClick={() => { setStockInput(stock); }}
                                className={`px-3 py-[2.06px] cursor-pointer text-sm flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'hover:bg-muted'}`}
                              >
                                <span>{stock}</span>
                                {isSelected && <span className="text-primary text-xs">✓</span>}
                              </div>
                            );
                          })}
                          {filteredStocks.length > 20 && (
                            <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-t border-[#3a4252]">
                              ... and {filteredStocks.length - 20} more stocks
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="px-3 py-[2.06px] text-xs text-muted-foreground border-b border-[#3a4252] sticky top-0 bg-popover">
                          Stocks (0)
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Date */}
          <div className="flex items-center gap-1 sm:gap-2">
            <label className="text-[12px] sm:text-[13px] font-medium text-white whitespace-nowrap">Date:</label>
            <div className="relative h-8 sm:h-9 w-[110px] sm:w-[140px] border border-[#3a4252] rounded-md overflow-hidden focus-within:ring-1 focus-within:ring-primary/50 bg-background">
              <input
                ref={dateInputRef}
                type="date"
                value={targetDate}
                onChange={e => setTargetDate(e.target.value)}
                onClick={() => (dateInputRef.current as any)?.showPicker?.()}
                className="absolute top-0 left-0 h-full w-full px-1.5 sm:px-2 bg-transparent text-[13px] sm:text-sm text-white focus:outline-none cursor-pointer border-0 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
            </div>
          </div>

          {/* Time range */}
          <div className="flex flex-row flex-wrap items-center gap-1 sm:gap-1.5">
            <div className="flex items-center gap-1 sm:gap-1.5">
              <label className="text-[12px] sm:text-[13px] font-medium text-white whitespace-nowrap">Time:</label>
              <input
                type="time"
                step="1"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                className="h-8 sm:h-9 w-[76px] sm:w-auto px-1 sm:px-2 bg-background border border-[#3a4252] rounded-md text-[11px] sm:text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
            </div>
            <div className="flex items-center gap-1 sm:gap-1.5">
              <span className="text-white/40 text-[11px] sm:text-xs">-</span>
              <input
                type="time"
                step="1"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                className="h-8 sm:h-9 w-[76px] sm:w-auto px-1 sm:px-2 bg-background border border-[#3a4252] rounded-md text-[11px] sm:text-xs text-white focus:outline-none focus:ring-1 focus:ring-primary/50 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
              />
            </div>
          </div>

          {/* Broker */}
          <div className="flex items-center gap-1 sm:gap-2">
            <label className="text-[12px] sm:text-[13px] font-medium text-white whitespace-nowrap">Broker:</label>
            <BrokerSelector
              selectedBrokers={selectedBrokers}
              setSelectedBrokers={setSelectedBrokers}
              availableBrokers={availableBrokers}
              isTrial={isTrial}
              showToast={showToast}
            />
          </div>

          {/* Board */}
          <div className="hidden sm:flex items-center gap-2">
            <label className="text-[13px] font-medium text-white whitespace-nowrap">Board:</label>
            <div className="relative">
              <select
                value={selectedBoard}
                disabled
                className="h-9 min-w-[60px] pl-3 pr-8 bg-background border border-[#3a4252] rounded-md text-sm text-white outline-none cursor-not-allowed appearance-none opacity-60"
              >
                <option value="RG">RG</option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* F/D */}
          <div className="flex items-center gap-1 sm:gap-2">
            <label className="text-[12px] sm:text-[13px] font-medium text-white whitespace-nowrap">F/D:</label>
            <div className="relative">
              <select
                value={selectedFD}
                onChange={e => setSelectedFD(e.target.value)}
                className="h-8 sm:h-9 min-w-[50px] sm:min-w-[80px] pl-2 sm:pl-3 pr-6 sm:pr-8 bg-background border border-[#3a4252] rounded-md text-[13px] sm:text-sm text-white outline-none cursor-pointer appearance-none"
              >
                <option value="All">All</option>
                <option value="F">F</option>
                <option value="D">D</option>
              </select>
              <ChevronDown className="absolute right-1.5 sm:right-2.5 top-1/2 -translate-y-1/2 w-3 sm:w-4 h-3 sm:h-4 text-muted-foreground pointer-events-none" />
            </div>
          </div>

          {/* Show Broker and BO&SO Checkboxes */}
          <div className="flex flex-col gap-1 ml-1 sm:ml-2 items-start justify-center">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showBroker} onChange={e => setShowBroker(e.target.checked)} className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-[#3a4252] bg-background text-primary focus:ring-primary/50 focus:ring-offset-background accent-primary" />
              <span className="text-[11px] sm:text-xs font-medium text-white whitespace-nowrap">Broker</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showOrderNo} onChange={e => setShowOrderNo(e.target.checked)} className="w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-[#3a4252] bg-background text-primary focus:ring-primary/50 focus:ring-offset-background accent-primary" />
              <span className="text-[11px] sm:text-xs font-medium text-white whitespace-nowrap">BO & SO</span>
            </label>
          </div>

          {/* Show button */}
          <Button
            onClick={handleShow}
            disabled={isLoading}
            className="h-[32px] sm:h-[34px] px-3 sm:px-4 min-w-[60px] sm:min-w-[72px] bg-white text-black hover:bg-gray-200 font-bold text-[13px] sm:text-sm transition-colors ml-1 sm:ml-2 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-black" /> : 'Show'}
          </Button>

          <div className="flex-1" />
        </div>
      </div>

      {/* Ticker header — outside zoom so it never shrinks */}
      <div className="bg-muted/50 border-y border-border w-full shrink-0 flex flex-col md:flex-row items-center gap-1 md:gap-4 px-2 md:pl-4 py-1 md:py-0">
        <h3 className="font-semibold text-sm whitespace-nowrap shrink-0 hidden md:block">
          {appliedStock} — BID-OFFER REPLAY
        </h3>
        {/* Free trial play-count badge */}
        {isTrial && trialPlayCountLoaded && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${trialPlayCount >= TRIAL_MAX_PLAYS
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
            }`}>
            FREE TRIAL: {Math.max(0, TRIAL_MAX_PLAYS - trialPlayCount)}x play tersisa
          </span>
        )}
        <div className="flex-1 w-full min-w-0">
          <MediaPlayer
            isPlaying={isPlaying}
            setIsPlaying={(value: boolean) => {
              if (value && !isPlaying) {
                // Starting playback — enforce trial limit
                attemptPlay();
              } else {
                setIsPlaying(value);
              }
            }}
            speed={speed}
            setSpeed={setSpeed}
            currentTime={currentTime}
            setCurrentTime={setCurrentTime}
            minTime={appliedStartSec}
            maxTime={maxTime}
          />
        </div>
      </div>

      {/* ── Main Grid (always visible) ── */}
      {/* Outer: measures available space */}
      <div ref={panelWrapperRef} className="flex-1 min-h-0 overflow-hidden relative">
        {/* Inner: all panels scaled proportionally when screen is narrow.
            Height uses measured px (not %) so flex layout resolves correctly after zoom. */}
        <div
          className="flex flex-col"
          style={{
            zoom: window.innerWidth >= 1024 ? panelScale : 1,
            width: (window.innerWidth >= 1024 && panelScale < 1) ? `${PANEL_TARGET_WIDTH}px` : '100%',
            height: (window.innerWidth >= 1024 && panelOuterH > 0) ? `${panelOuterH / panelScale}px` : '100%',
          }}
        >
          {/* Mobile Tabs */}
          <div className="flex lg:hidden bg-gray-900 p-1 gap-1 border-b border-gray-700 shrink-0">
            <button onClick={() => setMobileTab('buy')} className={`flex-1 py-2 text-[13px] font-bold rounded transition-colors ${mobileTab === 'buy' ? 'bg-green-700 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>Buy</button>
            <button onClick={() => setMobileTab('orderbook')} className={`flex-1 py-2 text-[13px] font-bold rounded transition-colors ${mobileTab === 'orderbook' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>Orderbook</button>
            <button onClick={() => setMobileTab('sell')} className={`flex-1 py-2 text-[13px] font-bold rounded transition-colors ${mobileTab === 'sell' ? 'bg-red-700 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>Sell</button>
            <button onClick={() => setMobileTab('trade')} className={`flex-1 py-2 text-[13px] font-bold rounded transition-colors ${mobileTab === 'trade' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>Trade</button>
          </div>

          {/* Panels */}
          <div className="flex-1 flex flex-col lg:grid lg:grid-cols-[0.85fr_1fr_0.85fr_0.95fr] lg:gap-x-1 min-h-0">
            {/* Buy Orders */}
            <div className={`h-full overflow-hidden flex-col lg:flex lg:border-r lg:border-[#2d3748] ${mobileTab === 'buy' ? 'flex' : 'hidden'}`}>
              <FilterBar label="Buy Orders" accent="buy" filter={buyFilter} setFilter={setBuyFilter} onApply={() => setBuyApplied(buyFilter)} />
              <div className="flex-1 min-h-0 overflow-hidden">
                <BuyOrders currentTime={orderbookTime} allOrders={filteredOrders} isPlaying={isPlaying} onTimeChange={setCurrentTime} ignoreTimeFilter={isPanelFilterActive(buyApplied) || isControlFilterActive} showBroker={appliedShowBroker} filter={buyApplied} />
              </div>
            </div>
            {/* Orderbook + DoneDetails */}
            <div className={`h-full overflow-hidden flex-col lg:flex lg:border-r lg:border-[#2d3748] ${mobileTab === 'orderbook' ? 'flex' : 'hidden'}`}>
              {/* Header Lot/Value/WAP pakai done trades (single-count) biar sama dengan Broker Summary.
                  Jangan pakai filteredOrders (synthetic, 1 trade = 2 event buy+sell -> double). */}
              <StockInfoHeader allOrders={filteredDoneOrders} currentTime={currentTime} prevClose={prevClose} stock={appliedStock} />
              <div className="min-h-0 lg:border-b border-[#2d3748] flex flex-col" style={{ flex: 48 }}>
                <OrderBook
                  currentTime={orderbookTime}
                  allOrders={filteredOrders}
                  prevClose={prevClose}
                  showBroker={appliedShowBroker}
                  isFullDataLoaded={isFullDataLoaded}
                  onBidLotClick={(price) => {
                    const f = { price: String(price), broker: '', message: 'Open' };
                    setBuyFilter(f);
                    setBuyApplied(f);
                  }}
                  onOfferLotClick={(price) => {
                    const f = { price: String(price), broker: '', message: 'Open' };
                    setSellFilter(f);
                    setSellApplied(f);
                  }}
                />
              </div>
              <div className="min-h-0 overflow-hidden hidden lg:block" style={{ flex: 52 }}>
                <DoneDetails currentTime={currentTime} allOrders={filteredDoneOrders} prevClose={prevClose} />
              </div>
            </div>
            {/* Sell Orders */}
            <div className={`h-full overflow-hidden flex-col lg:flex lg:border-r lg:border-[#2d3748] ${mobileTab === 'sell' ? 'flex' : 'hidden'}`}>
              <FilterBar label="Sell Orders" accent="sell" filter={sellFilter} setFilter={setSellFilter} onApply={() => setSellApplied(sellFilter)} />
              <div className="flex-1 min-h-0 overflow-hidden">
                <SellOrders currentTime={orderbookTime} allOrders={filteredOrders} isPlaying={isPlaying} onTimeChange={setCurrentTime} ignoreTimeFilter={isPanelFilterActive(sellApplied) || isControlFilterActive} showBroker={appliedShowBroker} showOrderNo={appliedShowOrderNo} filter={sellApplied} />
              </div>
            </div>
            {/* Running Trade */}
            <div className={`h-full overflow-hidden flex-col lg:flex ${mobileTab === 'trade' ? 'flex' : 'hidden'}`}>
              <FilterBar
                label="Running Trade"
                filter={tradeFilter}
                setFilter={setTradeFilter}
                onApply={() => setTradeApplied(tradeFilter)}
                hideStatusSelect={true}
                customButton={
                  <button
                    onClick={() => setIsTradeGrouped(!isTradeGrouped)}
                    className={`h-[25px] px-2.5 text-white text-[14px] font-bold rounded whitespace-nowrap transition-colors ${isTradeGrouped ? 'bg-blue-600 hover:bg-blue-500' : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                  >
                    Group
                  </button>
                }
              />
              <div className="flex-1 min-h-0 overflow-hidden">
                <TradeHistory
                  ref={tradeHistoryRef}
                  stockCode={appliedStock}
                  currentTime={currentTime - 1}
                  allOrders={tradeOrders}
                  isPlaying={isPlaying}
                  setCurrentTime={setCurrentTime}
                  ignoreTimeFilter={isPanelFilterActive(tradeApplied) || isControlFilterActive}
                  isGrouped={isTradeGrouped}
                  showBroker={appliedShowBroker}
                  showOrderNo={appliedShowOrderNo}
                  prevClose={prevClose}
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
